import { memo, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Book,
  Check,
  ChevronDown,
  ChevronRight,
  Circle,
  FileText,
  Lightbulb,
  Search,
} from "lucide-react";
import { cn } from "@/lib/utils";
import BrickantaBrick from "@/assets/brickanta-brick-only.svg";
import type { ChatDisplayRow } from "../useGroupingStream";
import { FileChip } from "./FileChip";
import { QuestionCard } from "./QuestionCard";

interface ChatBubbleProps {
  row: ChatDisplayRow;
  highlightedFileIds?: string[];
  onHighlightFiles?: (fileIds: string[], pane?: "catalog" | "included") => void;
  onSelectQuestionOption?: (message: string) => void;
  isStreaming?: boolean;
}

const AgentMarkdown = memo(function AgentMarkdown({
  content,
  className,
}: {
  content: string;
  className?: string;
}) {
  return (
    <div className={className}>
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          h2: (props) => (
            <h2 className="text-ui-title-2 mb-3 mt-5 first:mt-0 last:mb-0" {...props} />
          ),
          h3: (props) => (
            <h3 className="text-ui-title-3 mb-2 mt-4 first:mt-0 last:mb-0" {...props} />
          ),
          h4: (props) => <h4 className="text-ui-label mb-2 mt-3 first:mt-0 last:mb-0" {...props} />,
          p: (props) => <p className="text-ui-body-sm mb-3 mt-3 first:mt-0 last:mb-0" {...props} />,
          ul: (props) => (
            <ul
              className="text-ui-body-sm my-3 ml-5 list-disc [&>li]:mt-1 first:mt-0 last:mb-0"
              {...props}
            />
          ),
          ol: (props) => (
            <ol
              className="text-ui-body-sm my-3 ml-5 list-decimal [&>li]:mt-1 first:mt-0 last:mb-0"
              {...props}
            />
          ),
          blockquote: (props) => (
            <blockquote
              className="text-ui-body-sm border-l-2 border-muted pl-4 italic"
              {...props}
            />
          ),
          hr: (props) => <hr className="my-3 border-b border-border" {...props} />,
          table: (props) => (
            <table
              className="my-3 w-full border-separate border-spacing-0 overflow-y-auto"
              {...props}
            />
          ),
          th: (props) => (
            <th
              className="text-ui-label bg-muted px-3 py-1.5 text-left first:rounded-tl-lg last:rounded-tr-lg"
              {...props}
            />
          ),
          td: (props) => (
            <td
              className="text-ui-body-xs border-b border-l px-3 py-1.5 text-left last:border-r"
              {...props}
            />
          ),
          tr: (props) => (
            <tr
              className="border-b [&:last-child>td:first-child]:rounded-bl-lg [&:last-child>td:last-child]:rounded-br-lg"
              {...props}
            />
          ),
          code: (props) => (
            <code className="text-ui-code rounded bg-muted px-1 py-0.5" {...props} />
          ),
          strong: (props) => <strong className="font-semibold" {...props} />,
          a: (props) => (
            <a className="text-primary font-medium underline underline-offset-4" {...props} />
          ),
        }}
      >
        {content}
      </Markdown>
    </div>
  );
});

function getStepIcon(step: { text: string; tool_name?: string }) {
  const name = step.tool_name;
  if (name === "read_document_pages" || name === "read")
    return <Book className="size-3 shrink-0" />;
  if (
    name === "browse_documents" ||
    name === "search_documents" ||
    name === "find_documents" ||
    name === "list_series" ||
    name === "explore" ||
    name === "search" ||
    name === "get_metadata"
  )
    return <Search className="size-3 shrink-0" />;
  if (name === "include_files" || name === "include_series")
    return <Check className="size-3 shrink-0" />;
  if (name === "exclude_files") return <Circle className="size-3 shrink-0 text-destructive" />;
  if (name === "inspect_document") return <FileText className="size-3 shrink-0" />;
  if (name === "get_coverage") return <Search className="size-3 shrink-0" />;
  return <Circle className="size-2 shrink-0" />;
}

/** Single step row — click to toggle full text when truncated. */
function StepLine({
  step,
  isActive,
  isNew,
}: {
  step: { text: string; tool_name?: string };
  isActive?: boolean;
  isNew?: boolean;
}) {
  const [textExpanded, setTextExpanded] = useState(false);
  const isReasoning = step.tool_name === "reasoning";
  return (
    <div
      className={cn(
        "flex cursor-pointer items-start gap-1.5 py-[3px] text-ui-body-xs",
        isReasoning ? "italic text-muted-foreground" : "text-foreground/80",
        isNew && "animate-step-in",
      )}
      onClick={() => setTextExpanded(!textExpanded)}
    >
      <span className="mt-[2px] flex size-3 shrink-0 items-center justify-center">
        {isReasoning ? <Circle className="size-1.5" /> : getStepIcon(step)}
      </span>
      <span className={cn("min-w-0 flex-1", !textExpanded && "truncate")}>{step.text}</span>
      {isActive && (
        <span className="mt-[3px] inline-block h-2 w-[2px] shrink-0 animate-thinking-glow rounded-full bg-warning" />
      )}
    </div>
  );
}

const COLLAPSED_MAX = 3;
/** Step row height (~24px). Expanded list is capped at 12 visible rows. */
const EXPANDED_MAX_H = `${12 * 24}px`;

export function ChatBubble({
  row,
  highlightedFileIds = [],
  onHighlightFiles,
  onSelectQuestionOption,
  isStreaming,
}: ChatBubbleProps) {
  const { t } = useTranslation();
  const isThinkingRow = row.icon !== "done";
  const [manualToggle, setManualToggle] = useState<boolean | null>(null);
  const stepsExpanded = manualToggle ?? false;
  const refs = row.metadata?.references || [];
  const question = row.metadata?.question;
  const hasSteps = row.steps.length > 0;

  if (row.role === "user") {
    return (
      <div className="flex justify-end px-3 py-1">
        <div className="max-w-[85%] rounded-3xl bg-muted px-5 py-2.5 text-foreground">
          <p className="text-ui-body-sm whitespace-pre-wrap">{row.content}</p>
          {refs.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {refs.map((ref) => (
                <FileChip
                  key={ref.file_id}
                  reference={ref}
                  highlighted={highlightedFileIds.includes(ref.file_id)}
                  onClick={(id) => onHighlightFiles?.([id], ref.source_pane)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (row.role === "system") {
    return (
      <div className="px-4 py-1">
        <p className="text-ui-body-xs text-muted-foreground italic">{row.content}</p>
      </div>
    );
  }

  // ── Thinking row ───────────────────────────────────────────────────
  if (isThinkingRow) {
    return (
      <ThinkingTrace
        row={row}
        hasSteps={hasSteps}
        isStreaming={!!isStreaming}
        stepsExpanded={stepsExpanded}
        onToggle={() => setManualToggle(!stepsExpanded)}
        question={question}
        onSelectQuestionOption={onSelectQuestionOption}
      />
    );
  }

  // ── Done / answer row ──────────────────────────────────────────────
  return (
    <div className="grid grid-cols-[auto_1fr] gap-3 px-3 py-1">
      <img src={BrickantaBrick} alt="Brickanta" className="size-6 shrink-0 mt-0.5" />
      <div>
        {hasSteps && (
          <button
            type="button"
            onClick={() => setManualToggle(!stepsExpanded)}
            className="mb-1.5 flex items-center gap-1 text-ui-body-xs text-muted-foreground hover:text-foreground"
          >
            <ChevronRight
              className={cn(
                "size-3 transition-transform duration-200",
                stepsExpanded && "rotate-90",
              )}
            />
            {t("procurement.packageDetail.agent.thinkingStates.thoughtSteps", {
              count: row.steps.length,
            })}
          </button>
        )}
        {hasSteps && stepsExpanded && (
          <div className="mb-2 space-y-0.5 border-l border-muted/50 pl-2">
            {row.steps.map((step, i) => (
              <div
                key={i}
                className="flex items-start gap-1.5 text-ui-body-xs text-muted-foreground"
              >
                {getStepIcon(step)}
                <span>{step.text}</span>
              </div>
            ))}
          </div>
        )}
        {row.content && <AgentMarkdown content={row.content} />}
        {refs.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {refs.map((ref) => (
              <FileChip
                key={ref.file_id}
                reference={ref}
                highlighted={highlightedFileIds.includes(ref.file_id)}
                onClick={(id) => onHighlightFiles?.([id], ref.source_pane)}
              />
            ))}
          </div>
        )}
        {question && onSelectQuestionOption && (
          <div className="mt-2">
            <QuestionCard
              question={{
                id: row.id,
                question: question.text,
                options: question.options,
                sequence: row.sort_order,
              }}
              onSelect={onSelectQuestionOption}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ── ThinkingTrace ────────────────────────────────────────────────────

function ThinkingTrace({
  row,
  hasSteps,
  isStreaming,
  stepsExpanded,
  onToggle,
  question,
  onSelectQuestionOption,
}: {
  row: ChatDisplayRow;
  hasSteps: boolean;
  isStreaming: boolean;
  stepsExpanded: boolean;
  onToggle: () => void;
  question: ChatDisplayRow["metadata"]["question"];
  onSelectQuestionOption?: (message: string) => void;
}) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevStepCount = useRef(row.steps.length);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    prevStepCount.current = row.steps.length;
  }, [row.steps.length]);

  const newStepIndex = prevStepCount.current < row.steps.length ? prevStepCount.current : -1;
  const totalSteps = row.steps.length;
  const hasOverflow = totalSteps > COLLAPSED_MAX;
  const collapsedSteps = hasOverflow ? row.steps.slice(-COLLAPSED_MAX) : row.steps;

  return (
    <div className="px-3 py-1">
      {!hasSteps && isStreaming && (
        <div className="flex items-center gap-1.5 px-2.5 py-1.5">
          <Lightbulb className="size-3 shrink-0 animate-thinking-glow text-warning" />
          <span className="text-ui-body-xs text-warning/70">
            {t("procurement.packageDetail.agent.thinkingStates.thinking")}
          </span>
        </div>
      )}
      {hasSteps && (
        <div
          className={cn(
            "overflow-hidden rounded-lg border transition-colors duration-300",
            isStreaming ? "border-warning-border/50" : "border-border",
          )}
        >
          {/* Header — lightbulb + chevron, clickable */}
          <button
            type="button"
            onClick={onToggle}
            className={cn(
              "flex w-full items-center gap-1.5 px-2.5 py-1 transition-colors",
              isStreaming ? "hover:bg-warning-muted/10" : "hover:bg-muted/20",
            )}
          >
            <Lightbulb
              className={cn(
                "size-3 shrink-0",
                isStreaming ? "animate-thinking-glow text-warning" : "text-muted-foreground/40",
              )}
            />
            <span
              className={cn(
                "text-ui-body-xs",
                isStreaming ? "text-warning/70" : "text-muted-foreground/40",
              )}
            >
              {t("procurement.packageDetail.agent.thinkingStates.thinking")}
            </span>
            <span className="flex-1" />
            <ChevronDown
              className={cn(
                "size-3 shrink-0 text-muted-foreground/30 transition-transform duration-200",
                !stepsExpanded && "-rotate-90",
              )}
            />
          </button>

          {/* Steps */}
          <div
            ref={stepsExpanded ? scrollRef : undefined}
            className={cn("px-3 pb-1.5", stepsExpanded && "overflow-y-auto overscroll-contain")}
            style={stepsExpanded ? { maxHeight: EXPANDED_MAX_H } : undefined}
          >
            {(stepsExpanded ? row.steps : collapsedSteps).map((step, i) => {
              const globalIndex = stepsExpanded ? i : totalSteps - collapsedSteps.length + i;
              return (
                <StepLine
                  key={globalIndex}
                  step={step}
                  isActive={isStreaming && globalIndex === totalSteps - 1}
                  isNew={isStreaming && globalIndex >= newStepIndex && newStepIndex >= 0}
                />
              );
            })}
            {!stepsExpanded && hasOverflow && (
              <button
                type="button"
                onClick={onToggle}
                className="mt-0.5 text-ui-body-xs text-muted-foreground/40 transition-colors hover:text-muted-foreground/70"
              >
                +{totalSteps - COLLAPSED_MAX}
              </button>
            )}
          </div>
        </div>
      )}

      {row.content && (
        <AgentMarkdown
          content={row.content}
          className="mt-2 italic text-muted-foreground [&_p]:text-ui-body-xs"
        />
      )}
      {question && onSelectQuestionOption && (
        <div className="mt-2">
          <QuestionCard
            question={{
              id: row.id,
              question: question.text,
              options: question.options,
              sequence: row.sort_order,
            }}
            onSelect={onSelectQuestionOption}
          />
        </div>
      )}
    </div>
  );
}
